"use client";

import { useEffect, useRef, useState } from "react";
import { useSession } from "next-auth/react";
import Link from "next/link";
import { Bell, Check, Loader2 } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

interface NotificationItem {
  id: string;
  type: string;
  title: string;
  message: string;
  link: string | null;
  isRead: boolean;
  createdAt: string;
}

export default function NotificationBell() {
  const { status } = useSession();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const load = async () => {
    try {
      const res = await fetch("/api/user/notifications");
      if (!res.ok) return;
      const data = await res.json();
      setItems(data.notifications || []);
      setUnreadCount(data.unreadCount || 0);
    } catch {
      // sessiz geç
    }
  };

  useEffect(() => {
    if (status !== "authenticated") return;
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [status]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const toggleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      setLoading(true);
      await load();
      setLoading(false);
    }
  };

  const markAllRead = async () => {
    setUnreadCount(0);
    setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
    try {
      await fetch("/api/user/notifications", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ markAll: true }),
      });
    } catch {
      // sessiz geç
    }
  };

  if (status !== "authenticated") return null;

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggleOpen} className="relative group" title="Bildirimler">
        <Bell size={22} strokeWidth={1.5} className="text-[#7A7670] group-hover:text-[#C9A84C] transition-colors" />
        {unreadCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 bg-[#C9A84C] text-black text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-3 w-80 max-h-[420px] overflow-y-auto bg-[#111114] border border-[rgba(201,168,76,0.15)] rounded-xl shadow-xl z-[200]"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-[rgba(201,168,76,0.08)]">
              <span className="text-[10px] uppercase tracking-widest text-[#C9A84C] font-bold">Bildirimler</span>
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="flex items-center gap-1 text-[9px] text-[#7A7670] hover:text-[#C9A84C] uppercase tracking-widest">
                  <Check size={11} /> Tümünü Okundu İşaretle
                </button>
              )}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 size={18} className="animate-spin text-[#C9A84C]" />
              </div>
            ) : items.length === 0 ? (
              <p className="text-[#7A7670] text-xs italic text-center py-10 px-4">Henüz bildiriminiz yok.</p>
            ) : (
              <div className="divide-y divide-[rgba(201,168,76,0.06)]">
                {items.map((n) => {
                  const content = (
                    <div className={`px-4 py-3 hover:bg-[rgba(201,168,76,0.03)] transition ${!n.isRead ? "bg-[#C9A84C]/[0.03]" : ""}`}>
                      <div className="flex items-start gap-2">
                        {!n.isRead && <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] mt-1.5 shrink-0" />}
                        <div className="min-w-0">
                          <p className="text-[#F4F0E8] text-xs font-medium">{n.title}</p>
                          <p className="text-[#7A7670] text-[11px] mt-0.5 leading-relaxed">{n.message}</p>
                          <p className="text-[9px] text-[#7A7670]/50 mt-1 uppercase tracking-widest">
                            {new Date(n.createdAt).toLocaleDateString("tr-TR")}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                  return n.link ? (
                    <Link key={n.id} href={n.link} onClick={() => setOpen(false)}>
                      {content}
                    </Link>
                  ) : (
                    <div key={n.id}>{content}</div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
